import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Send, Mail, ArrowLeft, Flag, Ban } from 'lucide-react';
import { messagesApi, blocksApi, files as filesApi } from '../services/api';
import { ReportDialog, BlockConfirmDialog } from '../components/CommunityDialogs';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

function ThreadList({ threads, active, onPick }) {
  if (threads === null) return <div className="p-6"><Loader2 className="animate-spin text-gray-400" size={18} /></div>;
  if (threads.length === 0) return <div className="p-6 text-sm text-gray-400" data-testid="messages-empty">No messages yet. Use "Message privately" on a Community post to start one.</div>;
  return threads.map(t => (
    <button key={t.userId} onClick={() => onPick(t.userId)} data-testid={`thread-${t.userId}`}
      className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-[#1f1f1f] hover:bg-white/5 ${active === t.userId ? 'bg-green-600/10' : ''}`}>
      <img src={filesApi.urlFor(t.avatar)} alt="" className="w-10 h-10 rounded-full object-cover" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white truncate">{t.name}</div>
        <div className="text-xs text-gray-400 truncate">{t.last}</div>
      </div>
      {t.blocked && <span className="text-[10px] font-semibold text-red-300 border border-red-500/40 rounded px-1">Blocked</span>}
      {t.unread > 0 && !t.blocked && <span className="text-[10px] font-bold bg-green-500 text-black rounded-full px-1.5 py-0.5" data-testid={`thread-unread-${t.userId}`}>{t.unread}</span>}
    </button>
  ));
}

function Conversation({ otherId, onBack, onSent, onBlockChange }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const endRef = useRef(null);

  const load = () => messagesApi.withUser(otherId).then(setData).catch(() => setData({ other: null, messages: [] }));
  useEffect(() => { setData(null); load(); }, [otherId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [data]);
  const refresh = () => { load(); onBlockChange(); };
  const unblock = async () => {
    await blocksApi.unblock(otherId);
    toast({ title: `${data.other?.name} is unblocked` });
    refresh();
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const m = await messagesApi.send(otherId, text.trim());
      setData(d => ({ ...d, messages: [...d.messages, m] }));
      setText('');
      onSent();
    } catch (err) {
      toast({ title: 'Message failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setSending(false); }
  };

  if (!data) return <div className="p-6"><Loader2 className="animate-spin text-gray-400" size={18} /></div>;
  const name = data.other?.name || 'Member';
  const locked = data.blockedByMe || data.blockedMe;
  return (
    <div className="flex flex-col h-full" data-testid="conversation">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1f1f1f]">
        <button onClick={onBack} className="md:hidden text-gray-300" data-testid="conversation-back"><ArrowLeft size={18} /></button>
        <div className="font-semibold text-white" data-testid="conversation-name">{name}</div>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden lg:inline text-[11px] text-gray-400">Private — only you two can see this</span>
          <button onClick={() => setReportOpen(true)} data-testid="conversation-report" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-[#2a2a2a] text-gray-200 hover:border-red-400"><Flag size={12} /> Report</button>
          {data.blockedByMe
            ? <button onClick={unblock} data-testid="conversation-unblock" className="text-xs px-2 py-1 rounded border border-[#2a2a2a] text-gray-200 hover:border-green-500">Unblock</button>
            : <button onClick={() => setBlockOpen(true)} data-testid="conversation-block" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10"><Ban size={12} /> Block</button>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {data.messages.map(m => (
          <div key={m.id} className={`flex ${m.fromUserId === user.id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.fromUserId === user.id ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-100'}`} data-testid={`message-${m.id}`}>{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {locked ? (
        <div className="p-3 border-t border-[#1f1f1f] text-sm text-center text-gray-200 bg-red-500/10" data-testid="conversation-blocked-note">
          {data.blockedMe ? `${name} has blocked you, so you can't message them.` : `You blocked ${name}. Unblock them to send a message.`}
        </div>
      ) : (
      <div className="flex gap-2 p-3 border-t border-[#1f1f1f]">
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} maxLength={2000}
          className="pbb-input flex-1" placeholder="Write a message..." data-testid="conversation-input" />
        <button onClick={send} disabled={sending} data-testid="conversation-send" className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-1 disabled:opacity-70">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
        </button>
      </div>
      )}
      <ReportDialog target={reportOpen ? { userId: otherId, name } : null} onClose={() => setReportOpen(false)} onDone={(b) => b && refresh()} />
      <BlockConfirmDialog target={blockOpen ? { userId: otherId, name } : null} onClose={() => setBlockOpen(false)} onBlocked={refresh} />
    </div>
  );
}

function BlockedList({ version, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => { blocksApi.list().then(setItems).catch(() => {}); }, [version]);
  if (!items.length) return null;
  const unblock = async (u) => { await blocksApi.unblock(u.id); toast({ title: `${u.name} is unblocked` }); onChange(); };
  return (
    <div className="pbb-card p-4" data-testid="blocked-list">
      <div className="font-semibold text-white text-sm">Blocked members</div>
      <div className="mt-2 divide-y divide-[#1f1f1f]">
        {items.map(u => (
          <div key={u.id} className="flex items-center gap-3 py-2" data-testid={`blocked-${u.id}`}>
            <img src={filesApi.urlFor(u.avatar)} alt="" className="w-8 h-8 rounded-full object-cover" />
            <div className="text-sm text-gray-100 flex-1">{u.name}</div>
            <button onClick={() => unblock(u)} data-testid={`unblock-${u.id}`} className="text-xs px-2 py-1 rounded border border-[#2a2a2a] text-gray-200 hover:border-green-500">Unblock</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMAIL_PREFS = [
  ['messageEmails', 'Email me about new messages', 'One email per conversation until you read it. The message itself stays private in the app.', 'message', 'Message emails'],
  ['commentEmails', 'Email me about comments on my posts', 'One email per post until you open its comments on Community.', 'comment', 'Comment emails'],
];

function EmailToggle() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { messagesApi.emailPrefs().then(setPrefs).catch(() => {}); }, []);
  if (prefs === null) return null;
  const toggle = async (key, label, v) => {
    setPrefs(p => ({ ...p, [key]: v }));
    try { setPrefs(await messagesApi.setEmailPrefs({ [key]: v })); toast({ title: `${label} ${v ? 'on' : 'off'}` }); }
    catch { setPrefs(p => ({ ...p, [key]: !v })); toast({ title: 'Could not save that setting' }); }
  };
  return (
    <div className="pbb-card divide-y divide-[#1f1f1f]" data-testid="email-prefs-card">
      {EMAIL_PREFS.map(([key, title, hint, tid, label]) => (
        <label key={key} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`${tid}-emails-row`}>
          <span><span className="font-semibold text-white">{title}</span><span className="block text-xs text-gray-300">{hint}</span></span>
          <Switch checked={prefs[key]} onCheckedChange={(v) => toggle(key, label, v)} data-testid={`${tid}-emails-toggle`} />
        </label>
      ))}
    </div>
  );
}

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const active = params.get('with');
  const [threads, setThreads] = useState(null);
  const [blockVersion, setBlockVersion] = useState(0);
  const load = () => messagesApi.threads().then(setThreads).catch(() => setThreads([]));
  useEffect(() => { load(); }, [active, blockVersion]);
  const bump = () => setBlockVersion(v => v + 1);

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="messages-page">
      <div>
        <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Mail size={22} className="text-green-400" /> Messages</h1>
        <p className="text-gray-300 text-sm">Private conversations with other members. Only you and the other person can read them.</p>
      </div>
      <div className="pbb-card p-0 overflow-hidden grid md:grid-cols-[300px_1fr] h-[70vh]">
        <div className={`border-r border-[#1f1f1f] overflow-y-auto ${active ? 'hidden md:block' : ''}`} data-testid="thread-list">
          <ThreadList threads={threads} active={active} onPick={id => setParams({ with: id })} />
        </div>
        <div className={`${active ? '' : 'hidden md:flex'} md:flex flex-col min-h-0`}>
          {active ? <Conversation key={`${active}-${blockVersion}`} otherId={active} onBack={() => setParams({})} onSent={load} onBlockChange={bump} />
            : <div className="m-auto text-sm text-gray-400 p-6">Pick a conversation</div>}
        </div>
      </div>
      <BlockedList version={blockVersion} onChange={bump} />
      <EmailToggle />
    </div>
  );
}
